import React, { useState, useEffect, useRef } from 'react';
import { VscAdd, VscTrash, VscEdit, VscCheck, VscClose } from 'react-icons/vsc';
import { getProjectStats, updateProject, uploadProjectImage } from '../../api/projects';
import './Projects.css';

function StatCard({ label, value, unit }) {
  return (
    <div className="stat-card">
      <span className="stat-value">{value ?? '—'}{unit ? <span className="stat-unit"> {unit}</span> : ''}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

function SpecLine({ label, value, unit }) {
  if (value == null || value === '') return null;
  return (
    <div className="spec-line">
      <span className="spec-line-label">{label}</span>
      <span className="spec-line-dots" />
      <span className="spec-line-value">{value}{unit ? <span className="spec-line-unit"> {unit}</span> : ''}</span>
    </div>
  );
}

// Clicking the ! cycles the notice colour
const SEVERITIES = ['yellow', 'red', 'blue'];
const nextSeverity = (s) => SEVERITIES[(SEVERITIES.indexOf(s) + 1) % SEVERITIES.length];

function Notice({ notice, canEdit, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(notice.text);
  const inputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => {
    if (text.trim()) onUpdate({ ...notice, text: text.trim() });
    setEditing(false);
  };

  const toggleSeverity = () => onUpdate({ ...notice, severity: nextSeverity(notice.severity) });

  return (
    <div className={`notice notice-${notice.severity}`}>
      <button className="notice-icon" onClick={canEdit ? toggleSeverity : undefined} title={canEdit ? 'Change colour (yellow → red → blue)' : undefined}>
        !
      </button>
      {editing ? (
        <input ref={inputRef} className="notice-input" value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setText(notice.text); setEditing(false); } }}
          onBlur={commit} />
      ) : (
        <span className="notice-text" onDoubleClick={canEdit ? () => setEditing(true) : undefined}>{notice.text}</span>
      )}
      {canEdit && !editing && (
        <div className="notice-actions">
          <button className="btn-icon" onClick={() => setEditing(true)} title="Edit"><VscEdit size={12} /></button>
          <button className="btn-icon btn-icon-danger" onClick={onDelete} title="Delete"><VscTrash size={12} /></button>
        </div>
      )}
    </div>
  );
}

const FLAG_EMOJI = {
  'Denmark':        '🇩🇰',
  'Norway':         '🇳🇴',
  'Sweden':         '🇸🇪',
  'United Kingdom': '🇬🇧',
  'Scotland':       '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  'Ireland':        '🇮🇪',
  'England':        '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
};

const CAN_EDIT_ROLES = new Set(['admin', 'engineer', 'manager', 'foreman']);

function ProjectInfo({ project, onOpenTab, onProjectSaved, role }) {
  const [stats, setStats]           = useState(null);
  const [notices, setNotices]       = useState(project?.notices || []);
  const [addingNotice, setAddingNotice] = useState(false);
  const [newNoticeText, setNewNoticeText] = useState('');
  const [newNoticeSev, setNewNoticeSev]   = useState('yellow');
  const [savingNotices, setSavingNotices] = useState(false);
  const [uploading, setUploading]   = useState(false);
  const fileInputRef = useRef(null);

  const canEdit = CAN_EDIT_ROLES.has(role);

  useEffect(() => {
    if (!project) return;
    setNotices(project.notices || []);
    getProjectStats(project._id).then(r => setStats(r.data)).catch(() => {});
  }, [project?._id]);

  const saveNotices = async (updated) => {
    setSavingNotices(true);
    try {
      const res = await updateProject(project._id, { notices: updated });
      setNotices(res.data.notices || []);
      onProjectSaved?.(res.data);
    } catch {}
    setSavingNotices(false);
  };

  const handleUpdateNotice = (updated) => {
    const next = notices.map(n => n._id === updated._id ? updated : n);
    setNotices(next);
    saveNotices(next);
  };

  const handleDeleteNotice = (id) => {
    const next = notices.filter(n => n._id !== id);
    setNotices(next);
    saveNotices(next);
  };

  const handleAddNotice = async () => {
    if (!newNoticeText.trim()) return;
    const next = [...notices, { text: newNoticeText.trim(), severity: newNoticeSev }];
    setNewNoticeText('');
    setNewNoticeSev('yellow');
    setAddingNotice(false);
    await saveNotices(next);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadProjectImage(project._id, file);
      onProjectSaved?.(res.data);
    } catch {}
    setUploading(false);
    e.target.value = '';
  };

  if (!project) {
    return <div className="empty-state"><span>Select a project from the left panel</span></div>;
  }

  const imageUrl = project.image ? project.image : null; // relative URL, proxied via /uploads

  return (
    <div className="project-info">

      {/* Header */}
      <div className="project-info-header">
        <div>
          <h1 className="project-info-name">{project.name}</h1>
          {project.shipName && <p className="project-info-sub">{project.shipName}</p>}
        </div>
        {canEdit && (
          <button className="btn-secondary" onClick={() => onOpenTab(`project-edit-${project._id}`, 'Edit Project', { type: 'project-edit', project })}>
            Edit Project
          </button>
        )}
      </div>

      {project.note && <p className="project-info-note">{project.note}</p>}

      {/* Notices */}
      {(notices.length > 0 || canEdit) && (
        <div className="notices-section">
          {notices.map(n => (
            <Notice key={n._id} notice={n} canEdit={canEdit}
              onUpdate={handleUpdateNotice} onDelete={() => handleDeleteNotice(n._id)} />
          ))}
          {addingNotice ? (
            <div className={`notice notice-${newNoticeSev} notice-new`}>
              <button className="notice-icon" onClick={() => setNewNoticeSev(nextSeverity)}
                title="Change colour (yellow → red → blue)">!</button>
              <input className="notice-input" autoFocus value={newNoticeText}
                onChange={e => setNewNoticeText(e.target.value)}
                placeholder="Notice text…"
                onKeyDown={e => { if (e.key === 'Enter') handleAddNotice(); if (e.key === 'Escape') { setAddingNotice(false); setNewNoticeText(''); } }} />
              <div className="notice-actions">
                <button className="btn-icon" onClick={handleAddNotice} title="Add"><VscCheck size={12} /></button>
                <button className="btn-icon" onClick={() => { setAddingNotice(false); setNewNoticeText(''); }} title="Cancel"><VscClose size={12} /></button>
              </div>
            </div>
          ) : canEdit && (
            <button className="btn-icon notice-add-btn" onClick={() => setAddingNotice(true)}>
              <VscAdd size={13} /> Add Notice
            </button>
          )}
        </div>
      )}

      {/* Ship photo */}
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
      <div className={`ship-banner${imageUrl ? '' : ' ship-banner-empty'}`}
        onClick={() => canEdit && fileInputRef.current?.click()}
        title={canEdit ? (imageUrl ? 'Click to change photo' : 'Click to upload photo') : undefined}>
        {imageUrl
          ? <img key={imageUrl} src={imageUrl} alt="Ship" className="ship-banner-img" />
          : <span className="ship-banner-placeholder">{uploading ? 'Uploading…' : canEdit ? '+ Upload ship photo' : ''}</span>
        }
        {canEdit && <div className="ship-banner-overlay">{uploading ? 'Uploading…' : imageUrl ? 'Change photo' : '+ Upload photo'}</div>}
      </div>

      {/* Specs: 2×2 grid — explicit placement so rows 1 and 2 align across both columns */}
      <div className="specs-columns">

        {/* col 1, row 1 — Vessel Details */}
        <div className="specs-section" style={{ gridColumn: 1, gridRow: 1 }}>
          <div className="specs-title">Vessel Details</div>
          <SpecLine label="NB No."         value={project.newbuildingNo ? `#${String(project.newbuildingNo).padStart(3, '0')}` : null} />
          <SpecLine label="Client"         value={project.client} />
          <SpecLine label="Type"           value={project.vesselType} />
          {project.flag && (
            <div className="spec-line">
              <span className="spec-line-label">Flag</span>
              <span className="spec-line-dots" />
              <span className="spec-line-value">
                {FLAG_EMOJI[project.flag] && <span className="flag-emoji">{FLAG_EMOJI[project.flag]}</span>}
                {project.flag}
              </span>
            </div>
          )}
          <SpecLine label="Port"           value={project.homePort} />
          <SpecLine label="Keel date"      value={project.keelDate     ? new Date(project.keelDate).toLocaleDateString()     : null} />
          <SpecLine label="Delivery date"  value={project.deliveryDate ? new Date(project.deliveryDate).toLocaleDateString() : null} />
          <SpecLine label="IMO number"     value={project.imoNumber} />
          <SpecLine label="Signal letters" value={project.signalLetters} />
        </div>

        {/* col 2, row 1 — Main Dimensions */}
        {(project.loa_m != null || project.lpp_m != null || project.breadth_m != null || project.depthMainDeck_m != null || project.depthShelterDeck_m != null || project.draught_m != null || project.frameSpacing_mm != null || project.crew != null) && (
          <div className="specs-section" style={{ gridColumn: 2, gridRow: 1 }}>
            <div className="specs-title">Main Dimensions</div>
            <SpecLine label="Length overall"     value={project.loa_m}              unit="m"  />
            <SpecLine label="Length between PP"  value={project.lpp_m}              unit="m"  />
            <SpecLine label="Breadth moulded"    value={project.breadth_m}          unit="m"  />
            <SpecLine label="Depth main deck"    value={project.depthMainDeck_m}    unit="m"  />
            <SpecLine label="Depth shelter deck" value={project.depthShelterDeck_m} unit="m"  />
            <SpecLine label="Scantling draught"  value={project.draught_m}          unit="m"  />
            <SpecLine label="Frame spacing"      value={project.frameSpacing_mm}    unit="mm" />
            <SpecLine label="Crew"               value={project.crew} />
          </div>
        )}

        {/* col 1, row 2 — Design */}
        {(project.ruleSet || project.ruleEdition || project.classNotation) && (
          <div className="specs-section" style={{ gridColumn: 1, gridRow: 2 }}>
            <div className="specs-title">Design</div>
            <SpecLine label="Rule set"       value={project.ruleSet} />
            <SpecLine label="Rule edition"   value={project.ruleEdition} />
            <SpecLine label="Class notation" value={project.classNotation} />
          </div>
        )}

        {/* col 2, row 2 — Capacities */}
        {(project.fuelOil_m3 != null || project.freshWater_m3 != null || project.forepeak_m3 != null || project.lubOil_m3 != null || project.hydraulicOil_m3 != null || project.urea_m3 != null || project.freshHold_m3 != null || project.rswTanks_m3 != null || project.tonnage_gt != null) && (
          <div className="specs-section" style={{ gridColumn: 2, gridRow: 2 }}>
            <div className="specs-title">Capacities</div>
            <SpecLine label="Fuel oil"                 value={project.fuelOil_m3}      unit="m³" />
            <SpecLine label="Fresh water"              value={project.freshWater_m3}   unit="m³" />
            <SpecLine label="Forepeak"                 value={project.forepeak_m3}     unit="m³" />
            <SpecLine label="Lubricating oil, storage" value={project.lubOil_m3}       unit="m³" />
            <SpecLine label="Hydraulic oil, storage"   value={project.hydraulicOil_m3} unit="m³" />
            <SpecLine label="Urea"                     value={project.urea_m3}         unit="m³" />
            <SpecLine label="Fresh hold"               value={project.freshHold_m3}    unit="m³" />
            <SpecLine label="RSW tanks"                value={project.rswTanks_m3}     unit="m³" />
            <SpecLine label="Tonnage"                  value={project.tonnage_gt}      unit="GT" />
          </div>
        )}

      </div>

      {/* Stats */}
      <div className="project-stats-grid">
        <StatCard label="Total Items" value={stats?.itemCount} />
        <StatCard label="Total Power" value={stats?.totalPower != null ? stats.totalPower.toFixed(1) : null} unit="kW" />
      </div>

      <div className="project-stats-section-label">Cables</div>
      <div className="project-stats-grid">
        <StatCard label="Planned" value={stats?.plannedM != null ? Math.round(stats.plannedM) : null} unit="m" />
        <StatCard label="Ordered" value={stats?.orderedM != null ? Math.round(stats.orderedM) : null} unit="m" />
        <StatCard label="Pulled"  value={stats?.pulledM  != null ? Math.round(stats.pulledM)  : null} unit="m" />
      </div>

      <div className="project-stats-section-label">Job Packages</div>
      <div className="project-stats-grid">
        <StatCard label="Planned"     value={stats?.jobPlanned} />
        <StatCard label="In Progress" value={stats?.jobInProgress} />
        <StatCard label="Completed"   value={stats?.jobCompleted} />
      </div>

    </div>
  );
}

export default ProjectInfo;
